import React from "react";
import { createPortal } from "react-dom";
import { subscribeToToasts } from "../lib/toast";

// Shows what lib/toast.js is told, top right. Mounted once, at the top of the app. Each one goes
// by itself, and a problem stays longest because someone has to read it and do something. Pointing
// at one holds it.
export const VISIBLE_MS = { success: 4000, info: 5000, warn: 7000, error: 9000 };
const MAX_SHOWN = 4;

const ICON_PATHS = {
  success: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  info: (
    <>
      <path d="M12 11v6" />
      <path d="M12 7.5v.01" />
    </>
  ),
  warn: (
    <>
      <path d="M12 6.5v7" />
      <path d="M12 17.5v.01" />
    </>
  ),
  error: (
    <>
      <path d="M12 6.5v7" />
      <path d="M12 17.5v.01" />
    </>
  ),
};

function Icon({ children }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

function Toast({ item, onDismiss }) {
  const timer = React.useRef(0);
  const hold = () => clearTimeout(timer.current);
  const start = React.useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onDismiss(item.id), VISIBLE_MS[item.tone]);
  }, [item.id, item.tone, onDismiss]);

  // Said again while it is still showing: the time starts over.
  React.useEffect(() => {
    start();
    return () => clearTimeout(timer.current);
  }, [start, item.shownAt]);

  return (
    <div
      className={`bdToast is-${item.tone}`}
      role={item.tone === "error" ? "alert" : "status"}
      onMouseEnter={hold}
      onMouseLeave={start}
      onFocus={hold}
      onBlur={start}
    >
      <span className="bdToast__icon" aria-hidden="true">
        <Icon>{ICON_PATHS[item.tone]}</Icon>
      </span>
      <span className="bdToast__text">{item.text}</span>
      <button type="button" className="bdToast__close" aria-label="Dismiss" onClick={() => onDismiss(item.id)}>
        <Icon>
          <path d="M7 7l10 10M17 7L7 17" />
        </Icon>
      </button>
    </div>
  );
}

export default function Toaster() {
  const [items, setItems] = React.useState([]);

  React.useEffect(
    () =>
      subscribeToToasts((next) => {
        setItems((current) => {
          const same = current.find((item) => item.tone === next.tone && item.text === next.text);
          if (same) return current.map((item) => (item === same ? { ...item, shownAt: next.id } : item));
          return [...current, { ...next, shownAt: next.id }].slice(-MAX_SHOWN);
        });
      }),
    [],
  );

  const dismiss = React.useCallback((id) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  return createPortal(
    <div className="bdToasts" aria-live="polite">
      {items.map((item) => (
        <Toast key={item.id} item={item} onDismiss={dismiss} />
      ))}
    </div>,
    document.body,
  );
}
